import { ReactElement, useCallback, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { FormikHelpers, useFormik } from 'formik';
import {
  Typography,
  DialogContent,
  Stack,
  TextField,
  DialogActions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Close as CloseIcon } from '@mui/icons-material';

import { useLazyRequest } from '~hooks/useLazyRequest';
import { createAndAssociateCompany } from '~services/partnerCompany';
import { PartnerCompanyRegistrationBody } from '~services/partnerCompany/types';
import { STATUS_CODES } from '~constants/statusCodes';
import { getHelperText, hasError } from '~utils/formHelpers';

import { DialogCreatePartnerCompanyProps } from './types';
import { FIELDS_NAME, initialValues, validationSchema } from './utils';
import {
  DialogCreatePartnerCompanyContainer,
  DialogCreatePartnerCompanyHeader,
  DialogCreatePartnerCompanyCloseIcon,
} from './styles';

const DialogCreatePartnerCompany = ({
  open,
  onClose,
  onSubmitSuccess,
}: DialogCreatePartnerCompanyProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const [, loadingCreate, , executeCreate] = useLazyRequest({
    request: createAndAssociateCompany,
    withPostSuccess: () => {
      const message = `El dador de carga se ha creado y asociado correctamente`;

      enqueueSnackbar(message, { variant: 'success' });

      onSubmitSuccess();
    },
    withPostFailure: (error) => {
      let errorMessage = 'Ha ocurrido un error, intente nuevamente';

      if (
        error.data?.data.errorCode === STATUS_CODES.userExist ||
        error.data?.data.errorCode === STATUS_CODES.userAlreadyExist
      ) {
        errorMessage =
          // eslint-disable-next-line max-len
          'El email que ingresado ya se encuentra registrado. Por favor, modifíquelo e intenté nuevamente.';
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });

  const onSubmit = useCallback(
    async (
      values: typeof initialValues,
      { setSubmitting }: FormikHelpers<typeof initialValues>,
    ) => {
      const body: PartnerCompanyRegistrationBody = {
        userData: {
          email: values[FIELDS_NAME.EMAIL],
          firstName: '',
          lastName: '',
          password: '',
          contactPhone: values[FIELDS_NAME.CONTACT_PHONE],
        },
        companyData: {
          logoFileId: null,
          name: values[FIELDS_NAME.COMPANY_NAME],
          cuit: '',
          countryId: 0,
          industryTypeId: 0,
          fleetSizeTypeId: 0,
        },
      };

      await executeCreate(body);

      setSubmitting(false);
    },
    [executeCreate],
  );

  const {
    errors,
    touched,
    getFieldProps,
    isSubmitting,
    resetForm,
    isValid,
    dirty,
    submitForm,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (!open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <DialogCreatePartnerCompanyContainer open={open}>
      <DialogCreatePartnerCompanyHeader>
        <Typography component="h2" variant="h6">
          Crear dador de carga
        </Typography>

        <DialogCreatePartnerCompanyCloseIcon
          aria-label="close"
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </DialogCreatePartnerCompanyCloseIcon>
      </DialogCreatePartnerCompanyHeader>

      <DialogContent dividers>
        <Stack spacing={2} flex={1}>
          <TextField
            fullWidth
            autoComplete="off"
            label="Nombre de empresa"
            error={hasError(touched, errors, FIELDS_NAME.COMPANY_NAME)}
            helperText={getHelperText(
              touched,
              errors,
              FIELDS_NAME.COMPANY_NAME,
            )}
            {...getFieldProps(FIELDS_NAME.COMPANY_NAME)}
          />

          <TextField
            fullWidth
            autoComplete="off"
            label="Email"
            error={hasError(touched, errors, FIELDS_NAME.EMAIL)}
            helperText={getHelperText(touched, errors, FIELDS_NAME.EMAIL)}
            {...getFieldProps(FIELDS_NAME.EMAIL)}
          />

          <TextField
            fullWidth
            autoComplete="off"
            label="Celular / Whatsapp (Opcional)"
            placeholder="Cod de área sin 0 + Nro sin 15"
            error={hasError(touched, errors, FIELDS_NAME.CONTACT_PHONE)}
            helperText={getHelperText(
              touched,
              errors,
              FIELDS_NAME.CONTACT_PHONE,
              'Cod de área sin 0 + Nro sin 15',
            )}
            {...getFieldProps(FIELDS_NAME.CONTACT_PHONE)}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={submitForm}
          loading={loadingCreate || isSubmitting}
          disabled={!dirty || !isValid}
        >
          Crear
        </LoadingButton>
      </DialogActions>
    </DialogCreatePartnerCompanyContainer>
  );
};

export default DialogCreatePartnerCompany;
